import dayjs from "dayjs";
import { Stack } from "@/core/components/ui/stack";
import type { EnrichedResult } from "@/payroll/editor/types";

const periodLabel = (period: { start_date: string; end_date: string }) => {
  const start = dayjs(period.start_date);
  const end = dayjs(period.end_date);
  if (!start.isValid() || !end.isValid())
    return `${period.start_date} to ${period.end_date}`;
  return `${start.format("MMM D")}–${end.format("MMM D, YYYY")}`;
};

const headline = (result: EnrichedResult) => {
  const where = result.period ? ` over ${periodLabel(result.period)}` : "";
  // A run that stopped proves nothing either way. It used to read the change
  // count, find the zero that a failed run leaves behind, and announce that
  // pay did not move, which is the one thing an unfinished calculation cannot
  // establish. The next line says what to do about it.
  if (result.error)
    return `I tried to check this${where} and the test stopped.`;
  const moved = (result.comparison?.summary?.value_changes ?? 0) > 0;
  return moved
    ? `I checked this${where} and pay moved.`
    : `I checked this${where} and pay did not move.`;
};

/**
 * What the test run found, as a sentence.
 *
 * It used to quote its own count: "30 numbers changed across 1 sheet". That
 * count came from the agent's inner loop, which may run against a captured
 * snapshot of the business's data rather than live. The side-by-side runs live,
 * so the two surfaces reported different numbers for one change, and the
 * conversation's was the stale one. There is now one count, in the one place
 * that computes it, and the run behind it starts the moment the edit is saved.
 *
 * It carried a "See it side by side" link too. A link in a reply is a promise
 * that there is something to see, and the live run takes minutes, so the link
 * delivered a spinner. The Pay tab marks itself when the run is ready; that is
 * the invitation, and it only appears when it is true.
 */
export const CalculationResult = ({ result }: { result: EnrichedResult }) => {
  const needsAttention = result.statements.some(
    (statement) => statement.label === "Needs correction",
  );
  return (
    <Stack
      gap="none"
      align="stretch"
      direction="column"
      className="flex flex-col gap-1.5"
    >
      <p>{headline(result)}</p>
      {!!result.error && (
        <p className="text-secondary-text">
          The test could not finish. Ask me to explain what stopped it.
        </p>
      )}
      {result.issueMessages.map((message, index) => (
        <p key={index} className="text-secondary-text">
          {message}
        </p>
      ))}
      {needsAttention && (
        <p className="text-secondary-text">
          A PayPack needs correction after this change.
        </p>
      )}
    </Stack>
  );
};
